/**
 * CIStatusCards (extracted from ReleaseOpsTab.jsx in iter84.59.19)
 *
 * Two sibling GitHub-status surfaces rendered inside Phase 2 of the
 * Release Ops runbook:
 *   * `RecentDeploysCardContent` — top 5 commits on `main` with CI
 *     pipeline status badge per commit. Drives Phase 2's CI verdict
 *     (the `<CollapsibleCard verdict={ciVerdict}>` wrapper that owns
 *     the card title outline).
 *   * `OpenPullRequestsCardContent` — open PRs (filtered to non-draft
 *     for the verdict). Mostly empty on Emergent-managed deploys
 *     since "Save to GitHub" commits straight to main, but useful
 *     when human contributors PR through branches.
 *
 * Both call `api.github*` (which talks to `/api/admin/github/*` →
 * the GitHub PAT-backed proxy on the backend). Both expose
 * `onVerdictChange(verdict)` so the parent CollapsibleCard can
 * outline with the right colour.
 *
 * Touched in iter84.59.19 only to relocate — zero behavior change.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCopy } from "lucide-react";
import { api } from "@/lib/api";
import { CopyDiagButton, buildDiagText, diagLine } from "./CopyDiagButton";

// iter86.35 — hoisted so the PROMOTE PR card reuses the same CI pills.
const CI_PILL = {
    success: "border-emerald-500/40 bg-emerald-500/15 text-emerald-400",
    failure: "border-red-500/50 bg-red-500/15 text-red-400",
    pending: "border-amber-500/40 bg-amber-500/15 text-amber-400",
    none: "border-zinc-700 bg-zinc-800/60 text-zinc-500",
    no_scope: "border-orange-500/50 bg-orange-500/15 text-orange-400",
};
const CI_LABEL = {
    success: "CI ✓",
    failure: "CI ✗",
    pending: "CI …",
    none: "no CI",
    no_scope: "PAT scope ↗",
};

export function RecentDeploysCardContent({ onVerdictChange }) {
    const [data, setData] = useState({ count: 0, rows: [] });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        api.githubRecentCommits()
            .then((r) => {
                if (cancelled) return;
                setData(r);
                // iter84.56.7-h — Map top commit's CI status to a verdict
                // the parent CollapsibleCard can outline with.
                const top = r?.rows?.[0];
                if (onVerdictChange) {
                    if (!top || top.ci_status === "no_scope") {
                        onVerdictChange("warn"); // PAT scope or unknown
                    } else if (top.ci_status === "success") {
                        onVerdictChange("clear");
                    } else if (top.ci_status === "failure") {
                        onVerdictChange("fail");
                    } else if (top.ci_status === "pending") {
                        onVerdictChange("warn");
                    } else {
                        onVerdictChange("info");
                    }
                }
            })
            .catch(() => { /* silent */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [onVerdictChange]);

    if (loading) return <p className="text-xs text-zinc-500 italic">Loading recent commits…</p>;
    if (data.rows.length === 0) return <p className="text-xs text-zinc-500 italic">No commits visible. PAT may be missing the Contents:Read scope.</p>;

    const allNoScope = data.rows.every((c) => c.ci_status === "no_scope");

    return (
        <div>
            {data.branch && data.branch !== "main" && (
                <p className="mb-2 text-[10px] text-zinc-500" data-testid="recent-deploys-branch-hint">
                    Showing commits on branch <code className="bg-zinc-800 px-1 rounded text-volt">{data.branch}</code> — external production deploys only from <code className="bg-zinc-800 px-1 rounded">main</code> after PROMOTE merges.
                </p>
            )}
            {allNoScope && (
                <div
                    data-testid="recent-deploys-pat-scope-warning"
                    className="mb-3 p-3 border-2 border-orange-500/40 bg-orange-500/10 text-orange-300 text-[11px] leading-relaxed"
                >
                    <strong className="block text-orange-200 mb-1 uppercase tracking-wider text-[10px]">
                        GitHub PAT needs Actions: Read permission
                    </strong>
                    Recent Deploys can read commits but not CI status. Add the
                    <strong> "Actions: Read-only"</strong> repository permission to the fine-grained PAT.
                </div>
            )}
            <ul className="space-y-1">
                {data.rows.length > 0 && (
                    <li className="flex justify-end">
                        <CopyDiagButton
                            testId="recent-deploys-copy-diag-btn"
                            getText={() => buildDiagText({
                                card: "Recent Deploys (CI status)",
                                verdict: data.rows[0]?.ci_status || "unknown",
                                summary: `top commit ${data.rows[0]?.short_sha || "?"}`,
                                lines: data.rows.map((c) =>
                                    diagLine(c.ci_status || "none", `${c.short_sha} ${c.message}`, c.date ? new Date(c.date).toISOString() : "")),
                            })}
                        />
                    </li>
                )}
                {data.rows.map((c) => (
                    <li key={c.sha} data-testid={`deploy-row-${c.short_sha}`} className="flex items-center justify-between text-[11px] py-1 border-b border-[#1a1a1a] last:border-b-0">
                        <span className="flex items-center gap-2 min-w-0">
                            <span className={`pill text-[9px] font-bold uppercase border ${CI_PILL[c.ci_status] || CI_PILL.none}`}>
                                {CI_LABEL[c.ci_status] || "no CI"}
                            </span>
                            <code className="text-zinc-400 text-[10px]">{c.short_sha}</code>
                            <span className="text-white truncate" title={c.message}>{c.message}</span>
                            <span className="text-zinc-600 shrink-0">{c.date ? new Date(c.date).toLocaleString() : ""}</span>
                        </span>
                        <a href={c.html_url} target="_blank" rel="noopener noreferrer" data-testid={`open-commit-${c.short_sha}-btn`} className="brutal-btn-ghost text-[10px] py-1 px-2 text-volt hover:underline shrink-0 ml-2">
                            View →
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function OpenPullRequestsCardContent({ onVerdictChange }) {
    const [data, setData] = useState({ count: 0, rows: [] });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        api.githubOpenPrs()
            .then((r) => {
                if (cancelled) return;
                setData(r);
                if (onVerdictChange) {
                    // iter86.35 — PROMOTE PR verdict (gitops mode): mirror the
                    // PR head's CI status; a merged PR (loop closed) or a
                    // legacy empty list is clear; "not opened yet" is info.
                    const open = (r?.rows || []).filter(p => !p.draft);
                    if (r?.gitops) {
                        if (open.length > 0) {
                            const s = open[0].ci_status;
                            onVerdictChange(s === "success" ? "clear" : s === "failure" ? "fail" : s === "pending" ? "warn" : "info");
                        } else {
                            onVerdictChange(r?.merged_recent ? "clear" : "info");
                        }
                    } else {
                        onVerdictChange(open.length > 0 ? "warn" : "clear");
                    }
                }
            })
            .catch(() => { /* silent */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [onVerdictChange]);

    if (loading) return <p className="text-xs text-zinc-500 italic">Loading…</p>;

    // iter86.35 — dev-branch GitOps: this card tracks the dev → main PROMOTE PR.
    if (data.rows.length === 0 && data.gitops) {
        if (data.merged_recent) {
            const m = data.merged_recent;
            // iter86.40 — click-to-copy the exact canon-merge ref so the
            // operator doesn't hand-type "PR #N · sha" into the Finalize card.
            const canonRef = `PR #${m.number} · ${m.short_merge_sha}`;
            const copyCanonRef = async () => {
                try {
                    await navigator.clipboard.writeText(canonRef);
                    toast.success(`Copied "${canonRef}"`);
                } catch (_err) {
                    window.prompt("Copy this canon-merge ref:", canonRef);
                }
            };
            return (
                <div data-testid="promote-pr-merged" className="text-[11px] space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="pill text-[9px] font-bold uppercase border border-emerald-500/40 bg-emerald-500/15 text-emerald-400">Merged ✓</span>
                        <code className="text-zinc-400 text-[10px]">#{m.number}</code>
                        <span className="text-white truncate" title={m.title}>{m.title}</span>
                        <a href={m.html_url} target="_blank" rel="noopener noreferrer" className="brutal-btn-ghost text-[10px] py-1 px-2 text-volt hover:underline shrink-0 ml-auto">View →</a>
                    </div>
                    <p className="text-zinc-400">
                        Latest <code className="bg-zinc-800 px-1 rounded">{data.head}</code> → <code className="bg-zinc-800 px-1 rounded">main</code> PROMOTE merge
                        {m.merged_at ? ` on ${new Date(m.merged_at).toLocaleString()}` : ""}. Merge SHA{" "}
                        <code className="bg-zinc-800 px-1 rounded text-volt" data-testid="promote-pr-merge-sha">{m.short_merge_sha}</code>
                        {" "}
                        <button
                            type="button"
                            data-testid="promote-pr-copy-canon-ref"
                            onClick={copyCanonRef}
                            title="Click to copy the merge ref"
                            className="inline-flex items-center gap-1 text-zinc-200 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 rounded px-1.5 py-0.5 font-mono text-[10px] transition-colors cursor-pointer align-middle"
                        >
                            {canonRef}
                            <ClipboardCopy className="w-3 h-3 text-zinc-400" />
                        </button>
                        {/* iter86.82 — canon-merge field retired: the PR + merge
                            commit ARE the audit record (Canon Sync). */}
                        {" "}— canon is in sync.
                    </p>
                </div>
            );
        }
        return (
            <p className="text-xs text-zinc-500 italic" data-testid="promote-pr-empty">
                No open <code className="text-[10px] bg-zinc-800 px-1 rounded">{data.head}</code> → <code className="text-[10px] bg-zinc-800 px-1 rounded">main</code> PROMOTE PR yet.
                Once 3.1/3.2 verify production, open it in GitHub: Pull requests → New PR → base <code className="text-[10px] bg-zinc-800 px-1 rounded">main</code> ← compare <code className="text-[10px] bg-zinc-800 px-1 rounded">{data.head}</code>.
            </p>
        );
    }
    if (data.rows.length === 0) return (
        <p className="text-xs text-zinc-500 italic">
            No open PRs against <code className="text-[10px] bg-zinc-800 px-1 rounded">{data.base || "main"}</code>. Expected for direct-push workflows (Emergent's "Save to GitHub" commits straight to the checks branch).
        </p>
    );
    return (
        <ul className="space-y-1">
            {data.rows.map((p) => (
                <li key={p.number} data-testid={`pr-row-${p.number}`} className="flex items-center justify-between text-[11px] py-1 border-b border-[#1a1a1a] last:border-b-0">
                    <span className="flex items-center gap-2 min-w-0">
                        {/* iter86.35 — CI status of the PR head SHA, same pills as Recent Commits */}
                        <span className={`pill text-[9px] font-bold uppercase border ${CI_PILL[p.ci_status] || CI_PILL.none}`} data-testid={`pr-ci-pill-${p.number}`}>
                            {CI_LABEL[p.ci_status] || "no CI"}
                        </span>
                        {p.draft && <span className="pill text-[9px] font-bold uppercase border bg-zinc-500/15 text-zinc-400 border-zinc-500/40">draft</span>}
                        <code className="text-zinc-400 text-[10px]">#{p.number}</code>
                        <span className="text-white truncate" title={p.title}>{p.title}</span>
                        {p.head_ref && p.base_ref && (
                            <code className="text-zinc-500 text-[10px] shrink-0">{p.head_ref} → {p.base_ref}</code>
                        )}
                        {p.short_head_sha && <code className="text-zinc-600 text-[10px] shrink-0">{p.short_head_sha}</code>}
                        <span className="text-zinc-600 shrink-0">{p.author}</span>
                    </span>
                    <a href={p.html_url} target="_blank" rel="noopener noreferrer" className="brutal-btn-ghost text-[10px] py-1 px-2 text-volt hover:underline shrink-0 ml-2">
                        View →
                    </a>
                </li>
            ))}
        </ul>
    );
}
